"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {approvalPresentation} from "./approvalPresentation";
import Button from "@/components/library/Button";
import type {ToolApprovalResponse} from "@/services/wingman";
import type {ApprovalBatch, ApprovalDecisionOutcome} from "@/types/chatMessages";
import {approvalCanBeApplied} from "@/services/wingman/tool-approval";

/**
 * Decides the reply's remaining approvals in one action. Decisions already made on
 * individual proposals are kept, so this only fills in the rest.
 */
export default function ApprovalBatchActions({pending, undecidedIds, submitting, disabled, onRespond}: ApprovalBatch & {
   disabled: boolean;
   onRespond: (decisions: ToolApprovalResponse[]) => Promise<ApprovalDecisionOutcome>;
}) {
   const {t} = useTranslation("ask");
   const remaining = pending.filter(part => undecidedIds.includes(part.data.approval.id));
   const [now, setNow] = useState(Date.now);
   const nextExpiry = Math.min(...remaining.flatMap(part => {
      const expiresAt = Date.parse(part.data.approval.proposal?.expiresAt ?? "");
      return expiresAt > now ? [expiresAt] : [];
   }));
   useEffect(() => {
      if (!Number.isFinite(nextExpiry)) return;
      const timer = setTimeout(() => setNow(Date.now()), Math.min(Math.max(0, nextExpiry - Date.now()), 2 ** 31 - 1));
      return () => clearTimeout(timer);
   }, [nextExpiry, now]);
   const partial = remaining.length < pending.length;
   const blocked = disabled || submitting || remaining.length === 0;
   const decideRemaining = (approved: boolean) => {
      // Re-check expiry at click time; a preview can expire while the bar is open.
      if (approved && !remaining.every(part => approvalCanBeApplied(part.data.approval, Date.now()) && !approvalPresentation(part.data).unavailable)) return;
      // Each approval's card reports a failed submission.
      onRespond(remaining.map(part => ({id: part.data.approval.id, approved}))).catch(() => undefined);
   };
   return <div className="flex flex-wrap items-center justify-end gap-2">
      {partial && <output className="mr-auto text-xs text-500">
         {t("toolApproval.decidedCount", {decided: pending.length - remaining.length, total: pending.length})}
      </output>}
      <Button type="tertiary" compact disabled={blocked} onClick={() => decideRemaining(false)}>
         {t(partial ? "toolApproval.rejectRemaining" : "toolApproval.rejectAll", {count: remaining.length})}
      </Button>
      <Button type="primary" compact
         disabled={blocked || !remaining.every(part => approvalCanBeApplied(part.data.approval, now) && !approvalPresentation(part.data).unavailable)}
         onClick={() => decideRemaining(true)}>
         {t(partial ? "toolApproval.approveRemaining" : "toolApproval.approveAll", {count: remaining.length})}
      </Button>
   </div>;
}
